import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin, Maximize, BedDouble, Home as HomeIcon, Check, ArrowLeft,
  Loader2, CheckCircle2, Zap, CalendarClock, HeartPulse, Wrench, ShieldCheck, Smartphone, FileDown,
} from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import Seo from "../components/Seo";
import { AdBanner } from "../components/AdBanner";
import MapView from "../components/MapView";
import { toast } from "sonner";
import api, { API } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const inputCls = "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

export default function AnnonceDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.listing(id).then(setL).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-40 pb-28 text-center">
        <h1 className="font-serif text-4xl text-white">Annonce introuvable</h1>
        <Link to="/annonces" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline"><ArrowLeft className="h-4 w-4" /> Retour aux annonces</Link>
      </div>
    );
  }
  if (!l) return <div className="pt-40 pb-28 text-center text-slate-500">Chargement…</div>;

  return (
    <div data-testid="annonce-detail-page" className="pt-28">
      <Seo title={l.title} path={`/annonces/${l.id}`} description={`${l.title} à ${l.city} — ${l.surface} m². ${(l.description || "").slice(0, 120)}`} />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <Link to="/annonces" data-testid="back-to-annonces" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Toutes les annonces
          </Link>
          <a
            href={`${API}/listings/${l.id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm font-medium text-gold transition-colors hover:bg-gold/10"
          >
            <FileDown className="h-4 w-4" /> Fiche PDF
          </a>
        </div>

        {/* Gallery */}
        <div className="mt-6 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Reveal className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src={l.images?.[active]} alt={l.title} className="aspect-[16/11] w-full object-cover" data-testid="gallery-main" />
            <span className={`absolute left-5 top-5 rounded-full px-3 py-1 text-xs font-semibold ${l.transaction === "vente" ? "bg-gold text-navy" : "bg-white/90 text-navy"}`}>
              {l.transaction === "vente" ? "À vendre" : "À louer"}
            </span>
          </Reveal>
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-2">
            {(l.images || []).map((img, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setActive(i)}
                className={`overflow-hidden rounded-2xl border transition-colors ${active === i ? "border-gold" : "border-white/10 hover:border-white/30"}`}
              >
                <img src={img} alt="" className="aspect-square w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-12 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <Reveal>
              <Overline>{l.property_type} · réf. {l.reference}</Overline>
              <h1 className="mt-4 font-serif text-4xl tracking-tight text-white md:text-5xl">{l.title}</h1>
              <p className="mt-2 flex items-center gap-2 text-slate-300"><MapPin className="h-4 w-4 text-gold" /> {l.city}</p>
              <p className="mt-6 font-serif text-4xl text-gold">
                {fmt(l.price)}{l.transaction === "location" && <span className="text-lg text-slate-400"> /mois</span>}
              </p>
            </Reveal>

            <Reveal className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { icon: Maximize, v: `${l.surface} m²`, k: "Surface" },
                { icon: HomeIcon, v: `${l.rooms}`, k: "Pièces" },
                { icon: BedDouble, v: `${l.bedrooms}`, k: "Chambres" },
                { icon: Zap, v: l.dpe || "—", k: "DPE" },
              ].map((s) => (
                <div key={s.k} className="rounded-2xl border border-white/10 bg-navy-surface/60 p-5">
                  <s.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <p className="mt-3 font-serif text-2xl text-white">{s.v}</p>
                  <p className="text-xs text-slate-400">{s.k}</p>
                </div>
              ))}
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="font-serif text-2xl text-white">Description</h2>
              <p className="mt-4 leading-relaxed text-slate-300">{l.description}</p>
            </Reveal>

            {l.features?.length > 0 && (
              <Reveal className="mt-10">
                <h2 className="font-serif text-2xl text-white">Points forts</h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {l.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-slate-300">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3 w-3" strokeWidth={2.5} /></span>{f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}

            {/* Carnet de Santé encart */}
            {l.speed_detresse && (
              <Reveal className="mt-12" data-testid="carnet-encart">
                <div className="overflow-hidden rounded-3xl border border-gold/30 bg-navy-elevated">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-gold/10 px-6 py-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-navy"><HeartPulse className="h-5 w-5" /></span>
                      <div>
                        <h2 className="font-serif text-2xl text-white">Carnet de Santé du Logement</h2>
                        <p className="text-xs text-gold">Bien suivi par Speed Détresse{l.carnet_since ? ` depuis ${l.carnet_since}` : ""}</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1 text-xs text-gold">
                      <ShieldCheck className="h-3.5 w-3.5" /> Historique vérifié
                    </span>
                  </div>

                  <div className="p-6">
                    <p className="text-sm text-slate-300">
                      Ce bien dispose d'une mémoire numérique : historique des interventions et de l'entretien réalisés.
                      Le détail complet et le contact se font via l'application Speed Détresse.
                    </p>

                    <div className="mt-6">
                      <div className="flex items-center gap-2 text-gold"><Wrench className="h-4 w-4" /><Overline>Historique des interventions</Overline></div>
                      {l.interventions?.length > 0 ? (
                        <ol className="mt-5 space-y-0" data-testid="interventions-list">
                          {l.interventions.map((it, i) => (
                            <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                              <div className="flex flex-col items-center">
                                <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gold" />
                                {i < l.interventions.length - 1 && <span className="mt-1 w-px flex-1 bg-white/10" />}
                              </div>
                              <div className="-mt-0.5">
                                <div className="flex flex-wrap items-baseline gap-x-3">
                                  <span className="text-sm font-semibold text-white">{it.type}</span>
                                  <span className="text-xs text-slate-500">{it.date}</span>
                                </div>
                                <p className="mt-0.5 text-sm text-slate-400">{it.description}</p>
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="mt-4 text-sm text-slate-500">Aucune intervention enregistrée pour le moment.</p>
                      )}
                    </div>

                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-navy px-4 py-3 text-xs text-slate-400">
                      <Smartphone className="h-4 w-4 flex-shrink-0 text-gold" />
                      Accès au carnet complet, aux documents et au contact : réservé à l'application Speed Détresse.
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Localisation */}
            <Reveal className="mt-12" data-testid="detail-map-section">
              <h2 className="font-serif text-2xl text-white">Localisation</h2>
              <p className="mt-2 text-sm text-slate-400">Secteur indicatif · {l.city}</p>
              <div className="mt-5 overflow-hidden rounded-3xl">
                <MapView listings={[l]} single height={340} zoom={14} />
              </div>
            </Reveal>
          </div>

          {/* Visit form (sticky) */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <VisitForm listing={l} />
            <div className="mt-6"><AdBanner index={1} /></div>
          </div>
        </div>

        <div className="py-16" />
      </div>
    </div>
  );
}

function VisitForm({ listing }) {
  const empty = { nom: "", email: "", telephone: "", date_souhaitee: "", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try {
      const r = await api.visit({
        ...f,
        hp,
        listing_id: listing.id,
        listing_ref: listing.reference,
        listing_title: listing.title,
      });
      setDone(r.reference);
      toast.success("Demande de visite envoyée !");
    } catch (err) { toast.error("Une erreur est survenue."); } finally { setBusy(false); }
  };

  if (done) return (
    <div data-testid="visit-success" className="rounded-3xl border border-gold/25 bg-navy-elevated p-8 text-center">
      <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.3} />
      <h3 className="mt-4 font-serif text-2xl text-white">Demande envoyée</h3>
      <p className="mt-2 text-sm text-slate-300">Nous vous recontactons pour organiser la visite.</p>
      <p className="mt-4 inline-block rounded-full border border-gold/40 px-4 py-2 font-mono text-sm text-gold" data-testid="visit-reference">Réf. {done}</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="visit-form" className="rounded-3xl border border-white/10 bg-navy-surface/60 p-7">
      <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="flex items-center gap-2 text-gold"><CalendarClock className="h-5 w-5" /><Overline>Demander une visite</Overline></div>
      <div className="mt-5 space-y-3">
        <input required data-testid="visit-nom" placeholder="Nom / prénom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
        <input required type="email" data-testid="visit-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        <input required data-testid="visit-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
        <input data-testid="visit-date" placeholder="Date souhaitée (ex : samedi matin)" value={f.date_souhaitee} onChange={(e) => setF({ ...f, date_souhaitee: e.target.value })} className={inputCls} />
        <textarea rows="3" data-testid="visit-message" placeholder="Votre message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />
        <label className="flex items-start gap-3 text-xs text-slate-400">
          <input type="checkbox" data-testid="visit-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
          J'accepte que mes données soient traitées pour organiser ma visite.
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="visit-submit" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer ma demande"}
      </button>
      <p className="mt-3 text-center text-xs text-slate-500">
        Première prise de contact. Le suivi (appel, messages, dossier complet) se poursuit dans l'application Speed Détresse.
      </p>
    </form>
  );
}
